import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const client = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

// Capacitor-friendly: also send Bearer from localStorage (native webviews may block cookies).
client.interceptors.request.use((config) => {
    const token = localStorage.getItem("rq_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

client.interceptors.response.use(
    (r) => r,
    (error) => {
        if (error.response?.status === 401) {
            const path = window.location.pathname;
            const isProtected = ["/dashboard", "/quotes", "/settings", "/notifications", "/onboarding"]
                .some((p) => path.startsWith(p));
            if (isProtected) localStorage.removeItem("rq_token");
        }
        return Promise.reject(error);
    }
);

// Convenience for legacy imports
export const api = client;

// ---------- Auth ----------
export const auth = {
    me: () => client.get("/auth/me"),
    login: (email, password) => client.post("/auth/login", { email, password }),
    register: (payload) => client.post("/auth/register", payload),
    logout: () => client.post("/auth/logout"),
    emergentExchange: (session_id) => client.post("/auth/oauth/session", { session_id }),
    googleAuthorize: (redirectUri) =>
        client.get(`/auth/google/authorize`, { params: { redirect_uri: redirectUri } }),
    googleCallback: (code, redirectUri) =>
        client.post("/auth/google/callback", { code, redirect_uri: redirectUri }),
};

// ---------- Business ----------
export const business = {
    get: () => client.get("/business"),
    create: (payload) => client.patch("/business", { ...payload, onboarded: true }),
    update: (payload) => client.patch("/business", payload),
};

// ---------- Public / currencies ----------
export const publicApi = {
    currencies: () => client.get("/public/currencies"),
    getQuote: (publicId) => client.get(`/public/q/${publicId}`),
    view: async (publicId, toCurrency) => {
        const { data } = await client.get(`/public/q/${publicId}`);
        // Flatten { quote, business, versions } -> flat object PublicQuote expects
        let flat = { ...(data.quote || {}), business: data.business || {}, versions: data.versions || [] };
        if (toCurrency && toCurrency !== flat.currency) {
            try {
                const { data: fx } = await client.get(`/public/q/${publicId}/fx`, { params: { to: toCurrency } });
                flat = {
                    ...flat,
                    currency: fx.currency,
                    subtotal: fx.subtotal,
                    tax_amount: fx.tax_amount,
                    total: fx.total,
                    discount: fx.discount,
                    line_items: (flat.line_items || []).map((li) => {
                        const fxi = (fx.line_items || []).find((x) => x.id === li.id);
                        return fxi ? { ...li, unit_price: fxi.unit_price, total: fxi.total } : li;
                    }),
                    fx_note: fx.note,
                };
            } catch { /* fallback: keep original currency */ }
        }
        // Compat: PublicQuote uses li.line_total and li.unit; adapt.
        flat.line_items = (flat.line_items || []).map((li) => ({
            ...li, line_total: li.total, unit: li.unit || "ea",
        }));
        return { data: flat };
    },
    accept: (publicId, customerName) =>
        client.post(`/public/q/${publicId}/accept`, { customer_name: customerName }),
    modify: (publicId, feedback) =>
        client.post(`/public/q/${publicId}/modify`, { feedback }),
    decline: (publicId, reason, feedback) =>
        client.post(`/public/q/${publicId}/decline`, { reason, feedback }),
    depositCheckout: (publicId) =>
        client.post(`/public/q/${publicId}/deposit/checkout`),
    depositStatus: (sessionId) =>
        client.get(`/public/deposit/status/${sessionId}`),
    schedule: (publicId, slotStart, notes) =>
        client.post(`/public/q/${publicId}/schedule`, { slot_start: slotStart, notes: notes || null }),
    fx: (publicId, to) => client.get(`/public/q/${publicId}/fx`, { params: { to } }),
};

// ---------- Quotes ----------
export const quotes = {
    list: (params) => client.get("/quotes", { params }),
    stats: () => client.get("/quotes/stats"),
    get: (id) => client.get(`/quotes/${id}`),
    versions: (id) => client.get(`/quotes/${id}/versions`),
    create: (payload) => client.post("/quotes", payload),
    update: (id, payload) => client.patch(`/quotes/${id}`, payload),
    send: (id) => client.post(`/quotes/${id}/send`),
    newVersion: (id, payload) => client.post(`/quotes/${id}/new-version`, payload),
    remove: (id) => client.delete(`/quotes/${id}`),
    uploadPhoto: (id, file) => {
        const fd = new FormData();
        fd.append("file", file);
        return client.post(`/quotes/${id}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    deletePhoto: (id, photoId) => client.delete(`/quotes/${id}/photos/${photoId}`),
    updatePhotoCaption: (id, photoId, caption) =>
        client.patch(`/quotes/${id}/photos/${photoId}`, { caption }),
    regeneratePhotoCaption: (id, photoId) =>
        client.post(`/quotes/${id}/photos/${photoId}/recaption`),
    photoUrl: (id, photoId) => `${API_BASE}/quotes/${id}/photos/${photoId}/view`,
    publicPhotoUrl: (publicId, photoId) => `${API_BASE}/public/q/${publicId}/photos/${photoId}`,
};

// ---------- Notifications ----------
export const notifications = {
    list: () => client.get("/notifications"),
    markRead: (id) => client.post(`/notifications/${id}/read`),
    markAllRead: () => client.post(`/notifications/read-all`),
};

// ---------- Templates ----------
export const templates = {
    list: () => client.get("/templates"),
    get: (id) => client.get(`/templates/${id}`),
    createQuote: (id) => client.post(`/templates/${id}/quote`),
};

// ---------- Voice ----------
export const voice = {
    transcribe: (blob) => {
        const fd = new FormData();
        fd.append("audio", blob, "quote.webm");
        return client.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    parse: (transcript, currency) =>
        client.post("/voice/parse-quote", { transcript, currency }),
    voiceToQuote: (blob) => {
        const fd = new FormData();
        fd.append("audio", blob, "quote.webm");
        return client.post("/voice/voice-to-quote", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
};

export const setSessionToken = (t) => {
    if (t) localStorage.setItem("rq_token", t);
    else localStorage.removeItem("rq_token");
};
